import React, { Dispatch } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFeature } from '@ole-ui/ole-ui-components';

import { LandingLayout } from 'components/Landing/LandingLayout';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { PremiumLandingTab } from './types';

type SidebarButtonsProps = {
  activeTab?: PremiumLandingTab;
  hasPremiumSessions: boolean;
  isEnrolling: boolean;
  onBulkScheduleButton: () => void;
  onCancelBulkScheduling: () => void;
  selectedPremiumSessions: Set<string>;
  selectionMode: boolean;
  setIsModalOpen: Dispatch<React.SetStateAction<boolean>>;
  totalSessions: number;
};

const SidebarButtons = ({
  activeTab,
  hasPremiumSessions,
  isEnrolling,
  onBulkScheduleButton,
  onCancelBulkScheduling,
  selectedPremiumSessions,
  selectionMode,
  setIsModalOpen,
  totalSessions,
}: SidebarButtonsProps) => {
  const { t } = useTranslation();

  const isSchedulingDesabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.ShowPremvtScheduleDisabled,
    defaultFlagValue: false,
  });

  if (!hasPremiumSessions || activeTab !== PremiumLandingTab.PremiumSessions) {
    return null;
  }

  if (selectionMode) {
    const selectedSessions = Array.from(selectedPremiumSessions)?.length;

    return (
      <>
        <span>
          <Trans
            components={{ bold: <b /> }}
            defaults="Scheduling <bold>{{selectedSessions}} out of {{totalSessions}}</bold> live sessions:"
            values={{ selectedSessions, totalSessions }}
          />
        </span>

        <LandingLayout.SidebarButton
          variant="primary"
          isLoading={isEnrolling}
          isDisabled={
            isEnrolling ||
            selectedPremiumSessions.size === 0 ||
            isSchedulingDesabled
          }
          onClick={() => setIsModalOpen(true)}
          data-analytics-id="confirm-schedule-btn-premium-landing-view-lp"
        >
          {t('Confirm')}
        </LandingLayout.SidebarButton>

        <LandingLayout.SidebarButton
          variant="secondary"
          onClick={onCancelBulkScheduling}
          data-analytics-id="cancel-bulk-schedule-btn-premium-landing-view-lp"
        >
          {t('Cancel bulk schedule')}
        </LandingLayout.SidebarButton>
      </>
    );
  }

  return (
    <LandingLayout.SidebarButton
      variant="primary"
      aria-disabled={selectionMode}
      isDisabled={isSchedulingDesabled}
      onClick={onBulkScheduleButton}
      data-analytics-id="bulk-schedule-btn-premium-landing-view-lp"
    >
      {t('Bulk schedule')}
    </LandingLayout.SidebarButton>
  );
};

export default SidebarButtons;
